import React, { useState } from 'react';
import { engineBadge, parseSeeds } from '../tdmUtils';
import { DATA_FACETS, activeFacetCount } from './landscapeFilters';

const Chip = ({ on, onClick, testId, children, title }) => (
  <button type='button' className={`tdm-chip${on ? ' tdm-chip--on' : ''}`} aria-pressed={on}
    onClick={onClick} data-testid={testId} title={title}>
    {children}
  </button>
);

const toggleIn = (list, value) => (list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

/**
 * Collapsed-by-default facet accordion under the Landscape search box: Data (masked / raw),
 * Engine, Database and a pasted list of key ids. `filters` / `onChange` are owned by the
 * Landscape; this panel only edits them. Toggle pattern copied from DataGraph's
 * LinkSuggestionsPanel so the two screens read the same.
 */
const FiltersPanel = ({ filters, facets, onChange, onClear }) => {
  const [open, setOpen] = useState(false);
  const f = filters || { data: [], engines: [], databases: [], keyIdsText: '' };
  const active = activeFacetCount(f);
  const keyIdCount = parseSeeds(f.keyIdsText).length;
  const engines = (facets && facets.engines) || [];
  const databases = (facets && facets.databases) || [];
  const set = (patch) => onChange({ ...f, ...patch });

  return (
    <div className='tdm-filters' data-testid='tdm-filters'>
      <button type='button' className='tdm-filters__toggle' aria-expanded={open}
        onClick={() => setOpen(!open)} data-testid='tdm-filters-toggle'>
        <span className='tdm-caret'>{open ? '▾' : '▸'}</span> Filters{active > 0 ? ` · ${active}` : ''}
      </button>

      {open && (
        <div className='tdm-filters__body'>
          <div>
            <div className='tdm-filters__label'>Data</div>
            <div className='tdm-chips'>
              {DATA_FACETS.map((d) => (
                <Chip key={d} on={f.data.includes(d)} testId={`tdm-chip-data-${d}`}
                  onClick={() => set({ data: toggleIn(f.data, d) })}>
                  {d === 'MASKED' ? 'Masked' : 'Raw'}
                </Chip>
              ))}
            </div>
          </div>

          <div>
            <div className='tdm-filters__label'>Engine</div>
            <div className='tdm-chips'>
              {engines.length === 0 && <span className='tdm-muted'>—</span>}
              {engines.map((e) => (
                <Chip key={e} on={f.engines.includes(e)} testId={`tdm-chip-engine-${e}`} title={e}
                  onClick={() => set({ engines: toggleIn(f.engines, e) })}>
                  <span className='tdm-badge tdm-chip__badge'>{engineBadge(e)}</span> {e.toUpperCase()}
                </Chip>
              ))}
            </div>
          </div>

          <div>
            <div className='tdm-filters__label'>Database</div>
            <div className='tdm-chips'>
              {databases.length === 0 && <span className='tdm-muted'>—</span>}
              {databases.map((db) => (
                <Chip key={db} on={f.databases.includes(db)} testId={`tdm-chip-db-${db}`} title={db}
                  onClick={() => set({ databases: toggleIn(f.databases, db) })}>
                  {db}
                </Chip>
              ))}
            </div>
          </div>

          <div>
            <div className='tdm-filters__label'>Key ids</div>
            <textarea className='tdm-filters__keyids' rows={3} value={f.keyIdsText}
              placeholder='Paste ids, one per line or comma-separated'
              onChange={(e) => set({ keyIdsText: e.target.value })} data-testid='tdm-keyids' />
            <div className='tdm-muted tdm-filters__help'>
              {keyIdCount} id{keyIdCount === 1 ? '' : 's'} · matches subsets seeded with any of them
            </div>
          </div>

          <div className='tdm-filters__foot'>
            <button type='button' className='tdm-link' onClick={onClear}
              data-testid='tdm-filters-clear'>Clear</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FiltersPanel;
